/**
 * ### NavMenuDisclosureState
 *
 * Which primary navigation group currently holds its submenu open, tracked per
 * input source so the pointer and the keyboard can each release the menu
 * without closing what the other still holds.
 * `dismissedIndex` remembers an Escape press so that focus staying inside the
 * same group does not reopen the submenu it just closed.
 */
export interface NavMenuDisclosureState {
  /**
   * ### hoveredIndex
   *
   * Group the pointer is over, or `undefined` when it rests outside the navigation.
   */
  hoveredIndex: number | undefined;

  /**
   * ### focusedIndex
   *
   * Group that contains keyboard focus, or `undefined` when focus is elsewhere.
   */
  focusedIndex: number | undefined;

  /**
   * ### dismissedIndex
   *
   * Group closed with Escape while focus remained inside it.
   * Cleared as soon as focus leaves that group or enters another.
   */
  dismissedIndex: number | undefined;
}

/**
 * ### NavMenuDisclosureAction
 *
 * Input events the navigation forwards to {@link navMenuDisclosureReducer}.
 * Pointer and focus events carry the index of the group they happened in.
 */
export type NavMenuDisclosureAction =
  | { type: 'pointerEnter'; index: number }
  | { type: 'pointerLeave' }
  | { type: 'focusEnter'; index: number }
  | { type: 'focusLeave'; index: number }
  | { type: 'dismiss'; index: number };

/**
 * ### INITIAL_NAV_MENU_DISCLOSURE_STATE
 *
 * Every submenu closed and nothing dismissed.
 */
export const INITIAL_NAV_MENU_DISCLOSURE_STATE: NavMenuDisclosureState = {
  hoveredIndex: undefined,
  focusedIndex: undefined,
  dismissedIndex: undefined,
};

/**
 * ### navMenuDisclosureReducer
 *
 * Applies one navigation input event to the disclosure state.
 * Pointer events only touch `hoveredIndex`, so mouse behaviour is unchanged by
 * whatever the keyboard is doing; focus events only touch `focusedIndex` and
 * the Escape memory.
 *
 * @param state Disclosure state before the event
 * @param action Input event to apply
 * @returns Disclosure state after the event
 */
export function navMenuDisclosureReducer(
  state: NavMenuDisclosureState,
  action: NavMenuDisclosureAction,
): NavMenuDisclosureState {
  switch (action.type) {
    case 'pointerEnter':
      return { ...state, hoveredIndex: action.index };
    case 'pointerLeave':
      return { ...state, hoveredIndex: undefined };
    case 'focusEnter':
      return {
        ...state,
        focusedIndex: action.index,
        // Focus moving to another group ends the Escape memory of the previous one
        dismissedIndex:
          state.dismissedIndex === action.index ? action.index : undefined,
      };
    case 'focusLeave':
      if (state.focusedIndex !== action.index) return state;
      return { ...state, focusedIndex: undefined, dismissedIndex: undefined };
    case 'dismiss':
      return {
        ...state,
        hoveredIndex: undefined,
        dismissedIndex: action.index,
      };
  }
}

/**
 * ### resolveOpenNavMenuIndex
 *
 * Derives which group, if any, shows its submenu.
 * The pointer wins while it rests on a group; otherwise the focused group is
 * open unless Escape dismissed it.
 *
 * @param state Current disclosure state
 * @returns Index of the open group, or `undefined` when every submenu is closed
 */
export function resolveOpenNavMenuIndex(
  state: NavMenuDisclosureState,
): number | undefined {
  if (state.hoveredIndex !== undefined) return state.hoveredIndex;
  if (state.focusedIndex === undefined) return undefined;
  if (state.focusedIndex === state.dismissedIndex) return undefined;

  return state.focusedIndex;
}
